/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  RequestTransactionsInterFace,
  TransactionsInitialProperties,
  TransactionsResponseInterface,
} from '../../interface/transactions-station-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import stationManagementApi from '../../utils/api/station-management';

// a thunk to fetch transactions
export const fetchTransactions = createAsyncThunk<
  TransactionsResponseInterface,
  RequestTransactionsInterFace
>('transactions/fetchTransactions', async (data: RequestTransactionsInterFace, { dispatch }) => {
  try {
    const response = await stationManagementApi.fetchTransactionList(removeEmptyKeys(data));
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// a thunk to fetch transaction filters
export const fetchTransactionsFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  { chargingStation: string }
>('transactions/fetchTransactionsFilters', async ({ chargingStation }, { dispatch }) => {
  if (!chargingStation) {
    throw new Error('ChargingStation is required');
  }
  try {
    const response = await stationManagementApi.fetchTransactionListFilter({ chargingStation });
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: TransactionsInitialProperties = {
  transactions: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const TransactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    /**
     * Update By Transaction filters
     * @param state
     * @param action
     */
    updateTransactionsFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },

    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableTransactionFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<TransactionsInitialProperties>) => {
    builder
      .addCase(fetchTransactions.pending, (state: TransactionsInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchTransactions.fulfilled,
        (
          state: TransactionsInitialProperties,
          action: PayloadAction<TransactionsResponseInterface>,
        ) => {
          state.loading = false;
          state.transactions = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchTransactions.rejected, (state: TransactionsInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchTransactionsFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchTransactionsFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...availableFilters, ...action.payload];
        },
      )
      .addCase(fetchTransactionsFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});
export const { updateTransactionsFilters, disableTransactionFiltersApplied, setSortData } =
  TransactionsSlice.actions;
export default TransactionsSlice.reducer;
